/**
 * FindingsTimelineCard -- Rich expandable card for the Findings Timeline
 *
 * Severity-tinted background, corner accent, status pill,
 * expand/collapse with framer-motion, inline editing, and action buttons.
 */

import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "@tanstack/react-router";
import { ChevronDown, ExternalLink } from "lucide-react";
import type { AuditDetailTab, Id } from "@/packages/types";
import type {
  SeverityType,
  FindingStatusType,
} from "@/packages/domains/findings";
import { Button, ConfirmDialog } from "@/components/ui";
import { springs } from "@/components/ui/motion";
import {
  useResolveFinding,
  useReopenFinding,
  useDeleteFinding,
} from "@/packages/features-shared";
import { useTranslation } from "@/packages/utils";
import {
  useSeverityConfig,
  useStatusConfig,
  SEVERITY_COLORS,
} from "./findings-constants";
import { FindingInlineEditor } from "../checklist/findings/FindingInlineEditor";

// ─── Types ───────────────────────────────────────────────────────────────────

interface FindingDoc {
  _id: Id<"findings">;
  title: string;
  observation: string;
  recommendation: string;
  severity: SeverityType;
  status: FindingStatusType;
  categoryId?: Id<"checklistCategories">;
  checklistItemId?: Id<"checklistItems">;
}

interface FindingsTimelineCardProps {
  finding: FindingDoc;
  categoryName: string;
  isEditing: boolean;
  onEdit: () => void;
  onEditSave: () => void;
  onEditCancel: () => void;
  checklistItemId?: Id<"checklistItems">;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function FindingsTimelineCard({
  finding,
  categoryName,
  isEditing,
  onEdit,
  onEditSave,
  onEditCancel,
  checklistItemId,
}: FindingsTimelineCardProps) {
  const { t } = useTranslation("audits");
  const [expanded, setExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const navigate = useNavigate({ from: "/$workspaceSlug/audits/$auditId/" });
  const resolveMutation = useResolveFinding();
  const reopenMutation = useReopenFinding();
  const deleteMutation = useDeleteFinding();
  const severityConfig = useSeverityConfig();
  const statusConfig = useStatusConfig();

  const colors = severityConfig[finding.severity];
  const statusStyle = statusConfig[finding.status] ?? statusConfig.open;
  const accentColor = SEVERITY_COLORS[finding.severity].dot;
  const isCritical = finding.severity === "critical";

  function handleDelete() {
    deleteMutation.mutateAsync({ id: finding._id });
    setShowDeleteConfirm(false);
  }

  function handleDeleteClick(e: React.MouseEvent) {
    e.stopPropagation();
    setShowDeleteConfirm(true);
  }

  function handleResolve(e: React.MouseEvent) {
    e.stopPropagation();
    resolveMutation.mutateAsync({ id: finding._id });
  }

  function handleReopen(e: React.MouseEvent) {
    e.stopPropagation();
    reopenMutation.mutateAsync({ id: finding._id });
  }

  function handleEdit(e: React.MouseEvent) {
    e.stopPropagation();
    onEdit();
  }

  function handleViewSource(e: React.MouseEvent) {
    e.stopPropagation();
    if (!checklistItemId) return;
    void navigate({
      search: {
        tab: "checklist" as AuditDetailTab,
        checklistItemId,
        detailTab: "questions",
      },
    });
  }

  if (isEditing) {
    return (
      <div
        className="rounded-xl border border-border/50 shadow-sm relative overflow-hidden bg-card"
        style={{
          padding: isCritical ? 20 : 16,
          paddingLeft: isCritical ? 23 : 19,
        }}
      >
        <div
          className="absolute top-0 left-0 bottom-0 w-[3px] rounded-l-xl"
          style={{ backgroundColor: accentColor }}
        />
        <FindingInlineEditor
          finding={finding}
          isEditing={isEditing}
          onSave={onEditSave}
          onCancel={onEditCancel}
        />
      </div>
    );
  }

  return (
    <>
      <motion.button
        type="button"
        className="group relative w-full text-left rounded-xl bg-card shadow-sm border border-border/50 transition-shadow hover:shadow-md cursor-pointer overflow-hidden"
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        onClick={() => setExpanded((prev) => !prev)}
      >
        {/* Left severity accent stripe — slides in on hover */}
        <motion.div
          className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-xl"
          style={{ backgroundColor: accentColor, originY: 0.5 }}
          initial={{ scaleY: 0 }}
          animate={{ scaleY: isHovered ? 1 : 0 }}
          transition={springs.snappy}
        />

        <div style={{ padding: isCritical ? 20 : 16 }}>
          {/* Top row: title + status */}
          <div className="flex items-start justify-between gap-3">
            <h4
              className={`font-semibold leading-snug ${
                isCritical ? "text-[15px]" : "text-[14px]"
              }`}
            >
              {finding.title}
            </h4>
            <span
              className="shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full"
              style={{
                backgroundColor: statusStyle.bg,
                color: statusStyle.text,
              }}
            >
              {statusStyle.label}
            </span>
          </div>

          {/* Observation excerpt (collapsed) */}
          {!expanded && finding.observation && (
            <p className="mt-1.5 text-xs text-muted-foreground line-clamp-2">
              {finding.observation}
            </p>
          )}

          {/* Tag bar */}
          <div className="mt-2.5 flex items-center gap-2 flex-wrap">
            <span
              className="text-[11px] font-medium px-2 py-0.5 rounded-full"
              style={{
                backgroundColor: colors.bg,
                color: colors.border,
              }}
            >
              {categoryName}
            </span>
            <span
              className="text-[11px] font-medium px-2 py-0.5 rounded-full"
              style={{
                backgroundColor: colors.bg,
                color: colors.border,
              }}
            >
              {colors.label}
            </span>
            {checklistItemId && (
              <button
                type="button"
                className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-0.5 transition-colors"
                onClick={handleViewSource}
              >
                <ExternalLink size={10} />
                {t("findings.card.viewSourceChecklistItem")}
              </button>
            )}
          </div>

          {/* Expand indicator */}
          <motion.div
            className="flex justify-center mt-2"
            animate={{ rotate: expanded ? 180 : 0 }}
            transition={springs.snappy}
          >
            <ChevronDown size={14} className="text-muted-foreground" />
          </motion.div>

          {/* Expanded content */}
          <AnimatePresence initial={false}>
            {expanded && (
              <motion.div
                key="expanded"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={springs.smooth}
                className="overflow-hidden"
              >
                <div className="pt-3 space-y-3">
                  <div className="h-px bg-border/50" />

                  {finding.observation && (
                    <div>
                      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                        {t("findings.card.observation")}
                      </p>
                      <p className="text-sm text-foreground/80 leading-relaxed">
                        {finding.observation}
                      </p>
                    </div>
                  )}

                  {finding.recommendation && (
                    <div>
                      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                        {t("findings.card.recommendation")}
                      </p>
                      <p className="text-sm text-foreground/80 leading-relaxed">
                        {finding.recommendation}
                      </p>
                    </div>
                  )}

                  <div className="h-px bg-border/50" />

                  {/* Actions */}
                  <div className="flex gap-2">
                    {finding.status === "open" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleResolve}
                        disabled={resolveMutation.isPending}
                      >
                        {t("findings.card.resolve")}
                      </Button>
                    )}
                    {finding.status === "resolved" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleReopen}
                        disabled={reopenMutation.isPending}
                      >
                        {t("findings.card.reopen")}
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={handleEdit}>
                      {t("findings.card.edit")}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleDeleteClick}
                      disabled={deleteMutation.isPending}
                    >
                      {t("findings.card.delete")}
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.button>

      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
      >
        <ConfirmDialog.Title>
          {t("findings.card.deleteConfirm")}
        </ConfirmDialog.Title>
        <ConfirmDialog.Body>
          <p className="text-[13px] text-secondary">{finding.title}</p>
        </ConfirmDialog.Body>
        <ConfirmDialog.Actions
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
          variant="danger"
          loading={deleteMutation.isPending}
        />
      </ConfirmDialog>
    </>
  );
}
